import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLocalStorage } from '../utils/useLocalStorage';

import type {
    Company,
    CompanyAttribute as CompanyAttributeType,
    CompanyManager as CompanyManagerType,
} from '@/types/directory/Company';

import { ATTRIBUTE_FAMILIES_WITH_OWN_BUCKET } from '@/constants/Filters';
import { getCompanyAttributeList, type CompanyAttributeMap } from '@/utils/company';

export type SelectedFilters = {
    departments: CompanyAttributeType[];
    employeeLevels: CompanyAttributeType[];
    jobTitles: CompanyAttributeType[];
    companyAddresses: CompanyAttributeType[];
    managers: CompanyManagerType[];
    customAttributes: CompanyAttributeType[];
    [key: string]: CompanyAttributeType[] | CompanyManagerType[];
};

export interface OnSelectCompanyAttributeOption {
    attribute: { name: string; values: CompanyAttributeType[] | CompanyManagerType[] };
    value: string;
}

interface UseCompanyAttributesParams {
    storageKey?: string;
    company?: Company;
    defaultFilters?: SelectedFilters;
    defaultSelectedAttributes?: CompanyAttributeMap;
}

interface FilterUpdateParams {
    prev: SelectedFilters;
    filterKey: string;
    value: string;
    selectedValue: CompanyAttributeType | CompanyManagerType;
}

export const defaultFiltersState: SelectedFilters = {
    departments: [],
    employeeLevels: [],
    jobTitles: [],
    companyAddresses: [],
    managers: [],
    customAttributes: [],
};

export const useCompanyAttributesFilters = ({
    company,
    defaultFilters,
    defaultSelectedAttributes,
    storageKey,
}: UseCompanyAttributesParams) => {
    const { value: storedFilters, setValue: setStoredFilters } = useLocalStorage<SelectedFilters>({
        key: storageKey,
        initialValue: defaultFiltersState,
    });

    const { value: storedAttributes, setValue: setStoredAttributes } =
        useLocalStorage<CompanyAttributeMap>({
            key: storageKey ? `${storageKey}-attributes` : undefined,
            initialValue: defaultSelectedAttributes ?? {},
        });

    const [selectedCompanyAttributes, setSelectedCompanyAttributes] = useState<CompanyAttributeMap>(
        () => defaultSelectedAttributes ?? storedAttributes,
    );
    const [filters, setFilters] = useState<SelectedFilters>(() => defaultFilters ?? storedFilters);

    const [hasUserModified, setHasUserModified] = useState(false);

    const { i18n } = useTranslation();
    const previousLanguage = useRef(i18n.language);

    useEffect(() => {
        if (previousLanguage.current === i18n.language) {
            return;
        }
        previousLanguage.current = i18n.language;

        const attributes = getCompanyAttributeList(company);
        const nextSelectedAttributes: CompanyAttributeMap = {};
        Object.keys(selectedCompanyAttributes).forEach((key) => {
            if (attributes[key]) {
                nextSelectedAttributes[key] = attributes[key];
            }
        });
        setSelectedCompanyAttributes(nextSelectedAttributes);
        setStoredAttributes(nextSelectedAttributes);
    }, [company, i18n.language, selectedCompanyAttributes]);

    const handleFilterUpdate = ({
        prev,
        filterKey,
        value,
        selectedValue,
    }: FilterUpdateParams): SelectedFilters => {
        const currentValues = prev[filterKey] ?? [];
        const valueExists = currentValues.some((v) => v.id === value);

        if (valueExists) {
            const updatedFilters = {
                ...prev,
                [filterKey]: currentValues.filter((v) => v.id !== value),
            } as SelectedFilters;

            setStoredFilters(updatedFilters);

            return updatedFilters;
        }

        const currentFilters = {
            ...prev,
            [filterKey]: [...currentValues, selectedValue],
        } as SelectedFilters;

        setStoredFilters(currentFilters);

        return currentFilters;
    };

    const onSelectCompanyAttribute = (value: string) => {
        setHasUserModified(true);
        const attributes = getCompanyAttributeList(company);
        const attribute = Object.entries(attributes).find(([, attr]) => attr.name === value);

        if (!attribute) {
            return;
        }

        const [key, attrValue] = attribute;

        setSelectedCompanyAttributes((prev) => {
            const currentMap = prev ?? {};

            if (currentMap[key]) {
                const isCustomAttribute = !ATTRIBUTE_FAMILIES_WITH_OWN_BUCKET.includes(key);
                const filterKey = isCustomAttribute ? 'customAttributes' : key;

                setFilters((prevFilters) => {
                    const updatedFilters = {
                        ...prevFilters,
                        [filterKey]: [],
                    };

                    setStoredFilters(updatedFilters as SelectedFilters);

                    return updatedFilters;
                });

                const newMap = { ...currentMap };
                delete newMap[key];
                setStoredAttributes(newMap);
                return newMap;
            }

            const newMap = {
                ...currentMap,
                [key]: attrValue,
            };
            setStoredAttributes(newMap);
            return newMap;
        });
    };

    const onSelectCompanyAttributeOption = ({
        value,
        attribute,
    }: OnSelectCompanyAttributeOption) => {
        setHasUserModified(true);
        const selectedValue = attribute.values.find((v) => v.id === value);

        if (!selectedValue) {
            return;
        }

        const attributes = getCompanyAttributeList(company);
        const attributeKey = Object.entries(attributes).find(
            ([, attr]) => attr.name === attribute.name,
        )?.[0];

        if (!attributeKey) {
            return;
        }

        const isCustomAttribute = !ATTRIBUTE_FAMILIES_WITH_OWN_BUCKET.includes(attributeKey);
        const filterKey = isCustomAttribute ? 'customAttributes' : attributeKey;

        setFilters((prev) => handleFilterUpdate({ prev, filterKey, value, selectedValue }));
    };

    const resetFilters = () => {
        setFilters(defaultFiltersState);
        setSelectedCompanyAttributes({});
        setStoredFilters(defaultFiltersState);
        setStoredAttributes({});
        setHasUserModified(false);
    };

    return {
        selectedCompanyAttributes,
        filters,
        hasUserModified,
        onSelectCompanyAttribute,
        onSelectCompanyAttributeOption,
        setSelectedCompanyAttributes,
        setFilters,
        resetFilters,
    };
};
